<template>
  <aside class="credential-sidebar">
    <!-- HEADER -->
    <div class="sidebar-header">
      <h2>Zugangsdaten</h2>
      <button class="close-btn" :disabled="isLoading" @click="$emit('close')">
        ✕
      </button>
    </div>
    <p class="description">
      Schreiben Sie Ihren Zugangsdaten hier. Die hier gespeicherten Werte sind
      privat und nur für Sie sichtbar.
    </p>

    <div class="credetial-section">
      <!-- MODAL -->
      <form
        v-if="showModal"
        @submit.prevent
        ref="credentialFormRef"
        class="add-credential-modal"
      >
        <div class="field">
          <label>Bezeichnung</label>
          <input
            type="text"
            autocomplete="off"
            v-model="form.keyname"
            required
          />
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <div class="field">
          <label>Benutzername</label>
          <input
            type="text"
            name="username"
            autocomplete="new-username"
            v-model="form.username"
            required
          />
        </div>

        <div class="field">
          <label>Passwort</label>
          <input
            type="password"
            name="password"
            autocomplete="new-password"
            v-model="form.password"
            required
          />
        </div>
        <div
          class="btn-wrapper d-flex gap-2 align-items-center justify-content-end"
        >
          <button class="save-btn" @click="saveCredential">
            <span v-if="!isLoading">Speichern</span>
            <div v-else class="progress-icon"></div>
          </button>
          <button
            :disabled="isLoading"
            class="save-btn"
            type="button"
            @click="onToggleAddModal"
          >
            abbrechen
          </button>
        </div>
      </form>

      <!-- TABLE SECTION -->
      <div
        v-if="!showModal && getCredentialList.length"
        class="credential-table-wrap"
      >
        <table>
          <thead>
            <tr>
              <th>Aktiv</th>
              <th>Name</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in getCredentialList"
              :key="item.id"
              class="position-relative"
            >
              <td>
                <CommonSwitch
                  v-model="item.active"
                  @onChange="onChangeActive(item)"
                />
              </td>
              <td>{{ item.keyname }}</td>
              <td>
                <div
                  class="task-tools-container d-flex gap-2 align-items-center justify-content-center"
                >
                  <i
                    class="fas fa-edit"
                    @click="onEditCredential(item)"
                    v-b-tooltip.hover
                    title="Bearbeiten"
                  ></i>

                  <i
                    class="fas fa-trash"
                    @click="onDeleteCredential(item.id)"
                    v-b-tooltip.hover
                    title="Löschen"
                  ></i>
                </div>
              </td>
              <td v-if="loadingItemIds.includes(item.id)" class="loader-cell">
                <div class="row-loader">
                  <div class="spinner"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- ADD BUTTON -->
      <div v-if="!showModal" class="add-wrapper">
        <button class="add-btn" @click="onToggleAddModal">+</button>
      </div>
    </div>
    <!-- STATIC SECTION -->
    <div class="guide-section">
      <h3>Zugriff auf gespeicherte Zugangsdaten</h3>
      <prism-editor
        ref="editor-read-only-input"
        class="code-editor"
        v-model="useGuides"
        :highlight="highlighter"
        :readonly="true"
        line-numbers
        width="100"
      ></prism-editor>
    </div>
  </aside>
</template>

<script>
import CommonSwitch from '@/components/widgets/CommonSwitch.vue';
import CustomModal from '@/components/modals/CustomModal.vue';
import { PrismEditor } from 'vue-prism-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'vue-prism-editor/dist/prismeditor.min.css';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';
import { mapGetters } from 'vuex';
import { getCETTimeStampFromDate, formatTablename } from '@/utils/helpers';
import customKeysDatabaseSchema from '@/assets/data-schemas/customKeysDatabaseSchema.json';
import {
  createDatabaseTable,
  updateOneItemField,
  getCustomDatabaseItem,
} from '@/services/customdb.service';
import { addTableItem, updateTableItem } from '@/services/userTable';
import { deleteDatabaseItem } from '@/services/axios/data.api';

export default {
  name: 'CodeCredentialSidebar',

  data() {
    return {
      showModal: false,
      isLoading: false,
      loadingItemIds: [],
      form: {
        keyname: null,
        username: null,
        password: null,
        workflowdataidlist: '',
      },
      errorMessage: '',
      useGuides: `# Zugriff auf gespeicherte Zugangsdaten
  creds = codelos_access.get("codelos_creds");
  username = creds["username"];
  password = creds["password"];
  
  # Beispiel:
  print(username,password);`,
    };
  },
  props: {
    processId: {
      type: Number | String,
      required: true,
    },
    credentialList: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    async saveCredential() {
      const credForm = this.$refs.credentialFormRef;

      if (!credForm.checkValidity()) {
        credForm.reportValidity();
        return;
      }

      const exists = this.credentialList.some((cred) =>
        cred.id == this.form.id
          ? false
          : cred?.keyname?.toLowerCase() === this.form?.keyname?.toLowerCase()
      );

      if (exists) {
        this.errorMessage = 'Dieser Name existiert bereits.';
        return;
      }

      this.errorMessage = '';
      try {
        this.isLoading = true;
        if (this.form.isEdit) {
          await this.updateCustomKeyDatabase(this.form);
          this.onToggleAddModal();
          return;
        }
        const customKeysDatabaseDetails = await this.getCustomKeysDatabase();
        const Payload = {
          ...JSON.parse(JSON.stringify(this.form)),
          workflowdataidlist: `${this.processId}`,
        };
        const response = await addTableItem(customKeysDatabaseDetails.name, {
          item: Payload,
        });
        this.showToaster('Ausgeführt !', 'Erfolgreich', 'success');
        if (response?.data?.item_id)
          await this.setCreatedCredential(response.data.item_id);
        this.onToggleAddModal();
      } catch (error) {
        this.showToaster(
          'Bitte beheben Sie den Fehler im Code !',
          'Fehlerhaft !',
          'danger'
        );
      } finally {
        this.isLoading = false;
      }
    },
    onEditCredential(item) {
      this.form = {
        id: item.id,
        isEdit: true,
        keyname: item.keyname,
        username: item.username,
        password: item.password,
        workflowdataidlist: item.workflowdataidlist,
      };
      this.showModal = true;
    },
    onToggleAddModal() {
      this.form = {
        keyname: null,
        username: null,
        password: null,
        workflowdataidlist: '',
      };
      this.showModal = !this.showModal;
    },
    highlighter(code) {
      return highlight(code, languages.py);
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
    async onChangeActive(cred) {
      const changeItem = this.credentialList.find(
        (item) => item.id === cred.id
      );
      changeItem.workflowdataidlist = changeItem.workflowdataidlist
        .split(',')
        .filter((flowId) => flowId != this.processId)
        .join(',');
      if (cred.active) {
        changeItem.workflowdataidlist = changeItem.workflowdataidlist
          .split(',')
          .concat(this.processId)
          .filter(Boolean)
          .join(',');
      }
      await this.updateOneCustomkeysItemField(
        changeItem.id,
        'workflowdataidlist',
        changeItem.workflowdataidlist
      );
    },
    async getCustomKeysDatabase() {
      try {
        if (this.getCustomKeysDBDetails) return this.getCustomKeysDBDetails;

        const databaseSchema = customKeysDatabaseSchema.map(
          (schema, index) => ({
            ...schema,
            id: getCETTimeStampFromDate() + (index + 1),
          })
        );
        const formData = new FormData();
        formData.append('caption', 'custom-keys');
        formData.append('add_only', false);
        formData.append('icon', null);
        formData.append(
          'tablename',
          this.getUsername + '_' + formatTablename('custom-keys')
        );
        formData.append('tabletype', 'custom-keys');
        formData.append(
          'layout',
          '{"tabs":[{"id":0,"rows":[{"id":0,"cols":[{"id":0}]}]}]}'
        );
        formData.append('database_type', 'DynamoDB');
        formData.append('type', 'custom-keys');
        formData.append('schema', JSON.stringify(databaseSchema));

        const createDatabaseResponse = await createDatabaseTable(formData);
        await this.$store.dispatch('fetchUserDataBase');
        return JSON.parse(createDatabaseResponse.data);
      } catch (error) {
        throw error;
      }
    },
    async updateOneCustomkeysItemField(itemId, fieldName, payload) {
      try {
        if (!itemId || !fieldName) return;
        this.loadingItemIds.push(itemId);
        await updateOneItemField(
          this.getCustomKeysDBDetails?.name,
          itemId,
          fieldName,
          { value: payload }
        );
      } catch (error) {
        console.log('update error', error);
      } finally {
        this.loadingItemIds = this.loadingItemIds.filter((id) => id != itemId);
      }
    },
    async updateCustomKeyDatabase(updateItem) {
      try {
        const changeItem = this.credentialList.find(
          (item) => item.id == updateItem.id
        );
        const payload = JSON.parse(
          JSON.stringify({ ...changeItem, ...updateItem })
        );
        delete payload.isEdit;

        await updateTableItem(this.getCustomKeysDBDetails?.name, {
          item: payload,
        });
        const editCredIndex = this.credentialList.findIndex(
          (cred) => cred.id == this.form.id
        );
        if (editCredIndex != -1) {
          this.$set(
            this.credentialList,
            editCredIndex,
            JSON.parse(JSON.stringify(payload))
          );
        }
      } catch (error) {
        console.log('update error', error);
      }
    },
    async onDeleteCredential(itemId) {
      try {
        this.loadingItemIds.push(itemId);
        await deleteDatabaseItem(this.getCustomKeysDBDetails?.name, itemId);
        const credIndex = this.credentialList.findIndex(
          (item) => item.id == itemId
        );
        this.credentialList.splice(credIndex, 1);
      } catch (error) {
        console.log('remove error', error);
      } finally {
        this.loadingItemIds = this.loadingItemIds.filter((id) => id != itemId);
      }
    },

    async setCreatedCredential(itemId) {
      try {
        if (!this.getCustomKeysDBDetails?.name || !itemId) return;
        const response = await getCustomDatabaseItem(
          this.getCustomKeysDBDetails.name,
          itemId
        );
        if (response?.data) this.credentialList.push(response.data);
      } catch (error) {
        console.log('set error', error);
      }
    },
  },
  computed: {
    ...mapGetters({
      databaseList: 'getDatabaseList',
      getUsername: 'getUserId',
    }),
    user() {
      return this.$store.state.user;
    },
    getCustomKeysDBDetails() {
      return this.databaseList.find(
        (database) => database.type == 'custom-keys'
      );
    },
    getCredentialList() {
      return this.credentialList.map((cred) => ({
        id: cred.id,
        active: cred?.workflowdataidlist?.split(',')?.includes(this.processId),
        keyname: cred.keyname,
        username: cred.username,
        password: cred.password,
        workflowdataidlist: cred.workflowdataidlist,
      }));
    },
  },
  components: { PrismEditor, CommonSwitch, CustomModal },
};
</script>

<style scoped>
.credential-sidebar {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 420px;
  background: #ffffff;
  box-shadow: -2px 0 18px rgba(0, 0, 0, 0.12);
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 9999;
}

.credential-sidebar .sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}
.credential-sidebar .sidebar-header h2 {
  font-size: 22px;
  font-weight: 500;
  line-height: 40px;
  margin: 0;
  color: #333343;
  width: 100%;
  text-align: center;
}
.credential-sidebar .sidebar-header .close-btn {
  border: none;
  background: none;
  font-size: 22px;
  cursor: pointer;
  color: #37352fa6;
}
.credential-sidebar .description {
  font-size: 12px;
  font-weight: 400;
  margin: 0;
  color: #5f697a;
}
.credential-sidebar .content-loading {
  min-height: 315px;
  max-height: 315px;
}
.credential-sidebar .credetial-section {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 315px;
}

.credential-sidebar .credential-table-wrap {
  max-height: 250px;
  min-height: 100px;
  overflow-y: auto;
}

.credential-sidebar table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.credential-sidebar table thead tr {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 2;
}

.credential-sidebar th,
.credential-sidebar td {
  padding: 8px;
  border-bottom: 1px solid #e5e5e5;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #37352fa6;
}
.credential-sidebar th {
  color: #333343;
}

.credential-sidebar table .task-tools-container i {
  font-size: 14px;
  cursor: pointer;
}
.credential-sidebar table .task-tools-container i:hover {
  color: #333343;
}

/* tr loading style */

.credential-sidebar table .loader-cell {
  position: absolute;
  inset: 0;
  padding: 0;
  margin: 0;
  z-index: 5;
}

.credential-sidebar table .row-loader {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.credential-sidebar table .spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  animation: spin-keyframe 0.8s linear infinite;
}

.credential-sidebar .add-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.credential-sidebar .add-wrapper .add-btn {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: black;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}
.credential-sidebar .add-wrapper .add-btn:hover {
  background: #292929;
}

.credential-sidebar .guide-section {
  max-height: 200px;
}
.credential-sidebar .guide-section h3 {
  font-size: 18px;
  font-weight: 500;
  line-height: 40px;
  color: #333343;
}
.credential-sidebar .guide-section ::v-deep .code-editor {
  border-radius: 5px;
}
.credential-sidebar .guide-section ::v-deep .prism-editor__container {
  min-height: 100%;
  max-height: 100%;
}

.credential-sidebar .add-credential-modal {
  width: 100%;
  background: white;
}

.credential-sidebar .add-credential-modal .field {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.credential-sidebar .add-credential-modal .field label {
  font-size: 14px;
  color: #5f697a;
  font-weight: 400;
  padding-bottom: 5px;
}
.credential-sidebar .add-credential-modal .field input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  width: 100%;
  font-size: 14px;
  color: #5f697a;
  background: #ffffff;
  font-weight: 400;
  height: 35px;
}
.credential-sidebar .add-credential-modal .field input:active {
  outline-color: #5f697a;
  background: #ffffff;
}
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #5f697a !important;
}

.credential-sidebar .add-credential-modal .error-text {
  color: #e63946;
  font-size: 12px;
  font-weight: 400;
  margin: 0;
  margin-top: 4px;
}

.credential-sidebar .add-credential-modal .save-btn {
  background: black;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  width: 100px;
  height: 35px;
}
.credential-sidebar .add-credential-modal .save-btn:disabled {
  background: rgb(56, 55, 55);
  cursor: not-allowed;
}

.credential-sidebar .add-credential-modal .save-btn .progress-icon {
  content: ' ';
  display: block;
  width: 25px;
  height: 25px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: spin-keyframe 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin-keyframe {
  to {
    transform: rotate(360deg);
  }
}
</style>
